import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSocket } from '../../hooks/useSocket';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  RefreshCw,
  History,
  FileText,
  Users,
  Contact,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Phone,
  MapPin,
  Banknote,
  UserCheck,
  ArrowUpRight,
  BarChart3,
  Eye,
} from 'lucide-react';
import {
  approvalService,
  contactService,
  prequalificationService,
  tendersService,
  sourcesService,
} from '../../services/api';
import { ROLE_LABELS } from '../../lib/constants';
import {
  ApproveRequestModal,
  RejectRequestModal,
  ApprovalHistoryModal,
  ProfileDetailModal,
  PrequalificationDetailModal,
  TenderDetailModal,
} from '../modals';

// Roles with approval privileges (matches RBAC)
const APPROVER_ROLES = ['CEO', 'GM_Operations', 'BM_Operations'];

export default function ApprovalsView({ currentUser, users = [] }) {
  const isApprover = APPROVER_ROLES.includes(currentUser?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const { socket } = useSocket();

  const urlType = searchParams.get('type')?.toUpperCase();
  const urlStatus = searchParams.get('status')?.toUpperCase();

  // Filters & Pagination
  const [activeTab, setActiveTab] = useState(() => {
    if (['PROFILE', 'PREQUALIFICATION', 'TENDER', 'ALL'].includes(urlType)) return urlType;
    return 'ALL';
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    if (['PENDING', 'APPROVED', 'REJECTED', 'ALL'].includes(urlStatus)) return urlStatus;
    return 'PENDING';
  });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(15);

  // Sync state if URL searchParams change
  useEffect(() => {
    if (urlType && ['PROFILE', 'PREQUALIFICATION', 'TENDER', 'ALL'].includes(urlType)) {
      setActiveTab(urlType);
      setPage(1);
    }
  }, [urlType]);

  useEffect(() => {
    if (urlStatus && ['PENDING', 'APPROVED', 'REJECTED', 'ALL'].includes(urlStatus)) {
      setStatusFilter(urlStatus);
      setPage(1);
    }
  }, [urlStatus]);

  // Data
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState({
    totalPending: 0,
    pendingProfiles: 0,
    pendingPrequalifications: 0,
    pendingTenders: 0,
  });
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  // Modals for approval actions
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);

  // Detail modals for full inspection & in-modal approval/rejection
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedPrequalification, setSelectedPrequalification] = useState(null);
  const [selectedTender, setSelectedTender] = useState(null);
  const [fetchingDetailId, setFetchingDetailId] = useState(null);
  const [sources, setSources] = useState([]);

  useEffect(() => {
    sourcesService.getAll()
      .then((data) => { if (Array.isArray(data)) setSources(data); })
      .catch((err) => console.error('Failed to load sources in ApprovalsView:', err));
  }, []);

  const loadCounts = useCallback(async () => {
    try {
      const res = await approvalService.getCounts();
      if (res.status && res.data) setCounts(res.data);
    } catch (err) {
      console.error('Failed to load approval counts:', err);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setActionError('');
    try {
      const res = await approvalService.getAll({
        page,
        limit,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        entityType: activeTab === 'ALL' ? undefined : activeTab,
        search,
      });
      if (res.status) {
        setRequests(res.requests || []);
        setTotal(res.total || 0);
        setTotalPages(res.totalPages || 1);
      }
    } catch (err) {
      console.error('Failed to load approvals:', err);
      setActionError(err.message || 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, activeTab, search]);

  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { loadRequests(); }, [loadRequests]);

  // Real-time live update for approval requests & counts
  useEffect(() => {
    if (!socket) return;
    const handleLiveRefresh = () => {
      loadCounts();
      loadRequests();
    };
    socket.on('approval_counts_updated', handleLiveRefresh);
    socket.on('notification', handleLiveRefresh);
    return () => {
      socket.off('approval_counts_updated', handleLiveRefresh);
      socket.off('notification', handleLiveRefresh);
    };
  }, [socket, loadCounts, loadRequests]);

  const handleApproveConfirm = async (data) => {
    if (!approveTarget) return;
    try {
      await approvalService.approve(approveTarget.id, data);
      setActionSuccess(`"${approveTarget.title}" approved successfully.`);
      setTimeout(() => setActionSuccess(''), 4000);
      loadRequests();
      loadCounts();
    } catch (err) { throw err; }
  };

  const handleRejectConfirm = async (data) => {
    if (!rejectTarget) return;
    try {
      await approvalService.reject(rejectTarget.id, data);
      setActionSuccess(`"${rejectTarget.title}" returned with rejection remarks.`);
      setTimeout(() => setActionSuccess(''), 4000);
      loadRequests();
      loadCounts();
    } catch (err) { throw err; }
  };

  const handleRefreshAll = useCallback(() => {
    loadRequests();
    loadCounts();
  }, [loadRequests, loadCounts]);

  const handleOpenDetail = async (req) => {
    if (!req) return;
    setFetchingDetailId(req.id);
    setActionError('');
    try {
      if (req.entityType === 'PROFILE') {
        const id = req.profileId || req.profile?.id || req.entityId;
        if (!id) throw new Error('Profile ID not found for this approval request.');
        const res = await contactService.getById(id);
        const data = res?.data || res;
        setSelectedProfile({
          ...data,
          source: typeof data.source === 'object' && data.source !== null ? (data.source.name || '') : (typeof data.source === 'string' ? data.source : '')
        });
      } else if (req.entityType === 'PREQUALIFICATION') {
        const id = req.prequalificationId || req.prequalification?.id || req.entityId;
        if (!id) throw new Error('Prequalification ID not found for this approval request.');
        const res = await prequalificationService.getById(id);
        const data = res?.data || res;
        setSelectedPrequalification({
          ...data,
          source: typeof data.source === 'object' && data.source !== null ? (data.source.name || '') : (typeof data.source === 'string' ? data.source : ''),
          services: (data.services || []).map(s => typeof s === 'object' && s !== null ? s.name : s).filter(Boolean)
        });
      } else if (req.entityType === 'TENDER') {
        const id = req.tenderId || req.tender?.id || req.entityId;
        if (!id) throw new Error('Tender ID not found for this approval request.');
        const res = await tendersService.getById(id);
        const data = res?.data || res;
        setSelectedTender({
          ...data,
          source: typeof data.source === 'object' && data.source !== null ? (data.source.name || '') : (typeof data.source === 'string' ? data.source : ''),
          services: (data.services || []).map(s => typeof s === 'object' && s !== null ? s.name : s).filter(Boolean)
        });
      } else {
        setActionError(`Unknown entity type: ${req.entityType}`);
      }
    } catch (err) {
      console.error('Failed to open detail modal:', err);
      setActionError(err.message || 'Failed to load details');
    } finally {
      setFetchingDetailId(null);
    }
  };

  const getEntityConfig = (type) => {
    switch (type) {
      case 'PROFILE':
        return { Icon: Contact, label: 'Profile', badge: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'PREQUALIFICATION':
        return { Icon: Users, label: 'Prequalification', badge: 'bg-amber-50 text-amber-700 border-amber-200' };
      case 'TENDER':
        return { Icon: FileText, label: 'Tender / Proposal', badge: 'bg-purple-50 text-purple-700 border-purple-200' };
      default:
        return { Icon: ClipboardCheck, label: type, badge: 'bg-gray-50 text-gray-700 border-gray-200' };
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'APPROVED':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20 whitespace-nowrap w-fit">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Approved
          </span>
        );
      case 'REJECTED':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 ring-1 ring-rose-600/20 whitespace-nowrap w-fit">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
            Rejected
          </span>
        );
      case 'PENDING':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 ring-1 ring-amber-600/20 whitespace-nowrap w-fit">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            Pending
          </span>
        );
    }
  };

  // ── Stat card definitions (same pattern as FollowUpView) ──────────────────
  const statCards = [
    {
      key: 'ALL',
      label: 'Total Pending',
      value: counts.totalPending,
      Icon: BarChart3,
      activeGradient: 'from-slate-700 to-slate-900',
      inactiveGradient: 'from-white to-slate-50',
      inactiveBorder: 'border-slate-200',
      inactiveText: 'text-slate-700',
      inactiveIcon: 'text-slate-500',
      inactiveIconBg: 'bg-slate-100',
      pulse: false,
    },
    {
      key: 'PROFILE',
      label: 'Profiles',
      value: counts.pendingProfiles,
      Icon: Contact,
      activeGradient: 'from-blue-500 to-indigo-600',
      inactiveGradient: 'from-white to-blue-50',
      inactiveBorder: 'border-blue-100',
      inactiveText: 'text-blue-700',
      inactiveIcon: 'text-blue-500',
      inactiveIconBg: 'bg-blue-100',
      pulse: false,
    },
    {
      key: 'PREQUALIFICATION',
      label: 'Prequalifications',
      value: counts.pendingPrequalifications,
      Icon: Users,
      activeGradient: 'from-amber-500 to-orange-500',
      inactiveGradient: 'from-white to-amber-50',
      inactiveBorder: 'border-amber-100',
      inactiveText: 'text-amber-700',
      inactiveIcon: 'text-amber-500',
      inactiveIconBg: 'bg-amber-100',
      pulse: counts.pendingPrequalifications > 0,
    },
    {
      key: 'TENDER',
      label: 'Tenders / Proposals',
      value: counts.pendingTenders,
      Icon: FileText,
      activeGradient: 'from-purple-500 to-violet-600',
      inactiveGradient: 'from-white to-purple-50',
      inactiveBorder: 'border-purple-100',
      inactiveText: 'text-purple-700',
      inactiveIcon: 'text-purple-500',
      inactiveIconBg: 'bg-purple-100',
      pulse: false,
    },
  ];

  const STATUS_FILTERS = [
    { id: 'PENDING', label: 'Pending' },
    { id: 'APPROVED', label: 'Approved' },
    { id: 'REJECTED', label: 'Rejected' },
    { id: 'ALL', label: 'All' },
  ];

  const getTabLabel = () => {
    const entityMap = {
      PROFILE: 'Profile',
      PREQUALIFICATION: 'Prequalification',
      TENDER: 'Tender / Proposal',
    };
    const statusMap = {
      PENDING: 'Pending',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
    };
    const s = statusMap[statusFilter] || '';
    const e = entityMap[activeTab] || 'All Modules';
    return s ? `${s} — ${e}` : e;
  };

  return (
    <div className="flex flex-col gap-5 min-h-0 max-h-[calc(100dvh-7rem)]">

      {/* ── Page Header ── */}
      <div className="flex items-center justify-between gap-3 flex-shrink-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
            Approval Center
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
            {counts.totalPending > 0
              ? `${counts.totalPending} pending · ${counts.pendingProfiles} profiles · ${counts.pendingPrequalifications} prequalifications · ${counts.pendingTenders} tenders`
              : 'All caught up — no pending approval requests'}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Pending alert pill */}
          {counts.totalPending > 0 && (
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs font-semibold text-amber-700">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              {counts.totalPending} pending
            </div>
          )}
          <button
            onClick={() => { loadRequests(); loadCounts(); }}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl border border-gray-200 bg-white shadow-sm transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ── Alert Banners ── */}
      {actionSuccess && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-sm flex items-center gap-2.5 animate-fade-in flex-shrink-0">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{actionSuccess}</span>
        </div>
      )}
      {actionError && (
        <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-sm flex items-center gap-2.5 animate-fade-in flex-shrink-0">
          <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
          <span>{actionError}</span>
        </div>
      )}

      {/* ── Stat Cards (clickable filter buttons like FollowUpView) ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 flex-shrink-0">
        {statCards.map(({
          key, label, value, Icon,
          activeGradient, inactiveGradient, inactiveBorder,
          inactiveText, inactiveIcon, inactiveIconBg, pulse
        }) => {
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => {
                setActiveTab(key);
                setPage(1);
                setSearchParams((prev) => {
                  const p = new URLSearchParams(prev);
                  if (key === 'ALL') p.delete('type');
                  else p.set('type', key);
                  return p;
                }, { replace: true });
              }}
              className={`relative p-2.5 sm:p-4 rounded-xl sm:rounded-2xl text-left transition-all duration-200 overflow-hidden ${
                isActive
                  ? `bg-gradient-to-br ${activeGradient} text-white shadow-md sm:shadow-lg`
                  : `bg-gradient-to-br ${inactiveGradient} border ${inactiveBorder} hover:shadow-md hover:-translate-y-0.5`
              }`}
            >
              {isActive && <div className="absolute inset-0 bg-white/5 rounded-xl sm:rounded-2xl" />}
              {/* Pulsing dot for pending items */}
              {pulse && !isActive && (
                <span className="absolute top-1.5 right-1.5 sm:top-3 sm:right-3 flex h-1.5 w-1.5 sm:h-2 sm:w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 sm:h-2 sm:w-2 bg-amber-500" />
                </span>
              )}
              <div className="flex items-center gap-2.5 sm:block">
                <div className="flex items-center justify-between sm:mb-3">
                  <div className={`w-8 h-8 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0 ${isActive ? 'bg-white/20' : inactiveIconBg}`}>
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : inactiveIcon}`} />
                  </div>
                  {isActive && <ArrowUpRight className="w-3.5 h-3.5 text-white/60 hidden sm:block" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-base sm:text-2xl font-bold leading-none sm:mb-1 ${isActive ? 'text-white' : inactiveText}`}>
                    {value}
                  </p>
                  <p className={`text-[11px] font-medium truncate mt-0.5 sm:mt-0 ${isActive ? 'text-white/80' : 'text-slate-400'}`}>
                    {label}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Main Panel ── */}
      <div className="flex-1 min-h-0 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">

        {/* Toolbar */}
        <div className="p-3 sm:px-4 sm:py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center justify-between sm:justify-start gap-2 min-w-0">
            {/* Title with icon */}
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0">
                <ClipboardCheck className="w-3 h-3 text-white" />
              </div>
              <h2 className="font-semibold text-slate-800 text-sm truncate">{getTabLabel()}</h2>
              <span className="flex-shrink-0 px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full text-[10px] font-bold">
                {total}
              </span>
            </div>

            {/* Status filter pill tabs */}
            <div className="flex items-center gap-0.5 sm:gap-1 bg-white border border-slate-200 rounded-lg p-0.5 flex-shrink-0">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setStatusFilter(s.id);
                    setPage(1);
                    setSearchParams((prev) => {
                      const p = new URLSearchParams(prev);
                      if (s.id === 'PENDING') p.delete('status');
                      else p.set('status', s.id);
                      return p;
                    }, { replace: true });
                  }}
                  className={`px-2 sm:px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap ${
                    statusFilter === s.id
                      ? 'bg-slate-800 text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-56">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search title, submitter..."
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="py-20 text-center space-y-3">
              <div className="w-7 h-7 border-[3px] border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs text-slate-400">Loading approval requests...</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="py-20 text-center space-y-2">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mx-auto">
                <ClipboardCheck className="w-6 h-6 text-slate-400" />
              </div>
              <h4 className="text-sm font-semibold text-slate-600 mt-3">No requests found</h4>
              <p className="text-xs text-slate-400 max-w-xs mx-auto">
                {statusFilter === 'PENDING'
                  ? 'All clear — no pending requests in this category.'
                  : 'No records match the selected filters.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Entity & Title</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Submitted By</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Resolved By</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {requests.map((req) => {
                    const entity = getEntityConfig(req.entityType);
                    const EntityIcon = entity.Icon;
                    const isCurrentFetching = fetchingDetailId === req.id;

                    return (
                      <tr
                        key={req.id}
                        onClick={() => handleOpenDetail(req)}
                        className={`hover:bg-blue-50/50 transition-colors cursor-pointer group ${
                          isCurrentFetching ? 'bg-blue-50/70' : ''
                        }`}
                      >
                        {/* Entity & Title */}
                        <td className="px-6 py-4">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg border shrink-0 mt-0.5 ${entity.badge}`}>
                              <EntityIcon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0 max-w-sm">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`inline-block text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${entity.badge}`}>
                                  {entity.label}
                                </span>
                                {isCurrentFetching && (
                                  <span className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium animate-pulse">
                                    <RefreshCw className="w-3 h-3 animate-spin" /> Opening...
                                  </span>
                                )}
                              </div>
                              <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate text-sm" title={req.title}>
                                {req.title || `Request #${req.id}`}
                              </p>
                              {req.profile && (
                                <div className="flex items-center gap-3 mt-1 flex-wrap text-xs text-gray-500">
                                  {req.profile.mobile && (
                                    <span className="flex items-center gap-1 font-mono">
                                      <Phone className="w-3.5 h-3.5 text-gray-400" />
                                      {req.profile.mobile}
                                    </span>
                                  )}
                                  {req.profile.city && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                      {req.profile.city}
                                    </span>
                                  )}
                                </div>
                              )}
                              {req.tender?.proposalValue && (
                                <span className="flex items-center gap-1 text-xs text-gray-500 mt-1 font-medium">
                                  <Banknote className="w-3.5 h-3.5 text-gray-400" />
                                  PKR {req.tender.proposalValue.toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Submitter */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="font-semibold text-gray-900 text-sm">{req.requestedBy?.username || '—'}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {req.requestedBy?.role ? ROLE_LABELS[req.requestedBy.role] || req.requestedBy.role : ''}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {req.createdAt ? new Date(req.createdAt).toLocaleDateString() : ''}
                          </p>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(req.status)}
                        </td>

                        {/* Resolved By */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {req.status === 'APPROVED' ? (
                            <div>
                              <span className="text-xs font-medium text-emerald-700 flex items-center gap-1.5">
                                <UserCheck className="w-3.5 h-3.5 text-emerald-600" />
                                {req.resolvedBy?.username || 'Management'}
                              </span>
                              {req.assignedSalesperson && (
                                <span className="text-xs text-gray-500 block mt-0.5">
                                  Assigned: <strong className="text-gray-700 font-semibold">{req.assignedSalesperson.username}</strong>
                                </span>
                              )}
                            </div>
                          ) : req.status === 'REJECTED' ? (
                            <span className="text-xs font-medium text-rose-600 flex items-center gap-1.5">
                              <XCircle className="w-3.5 h-3.5 text-rose-500" />
                              {req.resolvedBy?.username || 'Management'}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs italic">Awaiting decision</span>
                          )}
                        </td>

                        {/* Remarks */}
                        <td className="px-6 py-4 max-w-[220px]">
                          {req.remarks ? (
                            <p className="text-sm text-gray-600 truncate" title={req.remarks}>{req.remarks}</p>
                          ) : (
                            <span className="text-gray-300 text-sm">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-3 sm:p-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 flex-shrink-0 bg-slate-50/40">
            <span>Showing {requests.length} of {total}</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 transition-colors bg-white"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="px-3 font-semibold text-slate-700">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 transition-colors bg-white"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {approveTarget && (
        <ApproveRequestModal
          request={approveTarget}
          users={users}
          onConfirm={handleApproveConfirm}
          onClose={() => setApproveTarget(null)}
        />
      )}
      {rejectTarget && (
        <RejectRequestModal
          request={rejectTarget}
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}
      {historyTarget && (
        <ApprovalHistoryModal
          request={historyTarget}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {/* ── Entity Detail Modals for Inspection and Direct In-Modal Action ── */}
      {selectedProfile && (
        <ProfileDetailModal
          profile={selectedProfile}
          currentUser={currentUser}
          users={users}
          onClose={() => {
            setSelectedProfile(null);
            handleRefreshAll();
          }}
          onSave={handleRefreshAll}
          onRefresh={handleRefreshAll}
        />
      )}

      {selectedPrequalification && (
        <PrequalificationDetailModal
          prequalification={selectedPrequalification}
          currentUser={currentUser}
          sources={sources}
          onClose={() => {
            setSelectedPrequalification(null);
            handleRefreshAll();
          }}
          onSave={handleRefreshAll}
          onRefreshLeads={handleRefreshAll}
          onRefreshTenders={handleRefreshAll}
        />
      )}

      {selectedTender && (
        <TenderDetailModal
          tender={selectedTender}
          currentUser={currentUser}
          onClose={() => {
            setSelectedTender(null);
            handleRefreshAll();
          }}
          onSave={handleRefreshAll}
          onDelete={() => {
            setSelectedTender(null);
            handleRefreshAll();
          }}
          onRefreshTenders={handleRefreshAll}
        />
      )}
    </div>
  );
}
